import { type Control, type Path } from 'react-hook-form'
import { cn } from '~/shadcn/lib/utils'
import { Input } from '~/shadcn/ui/input'
import { FormField, FormItem } from '~/shadcn/ui/form/form'
import { FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shadcn/ui/select'
import { usePhonePrefixes } from '~/@mid_settings/phone-prefixes/usePhonePrefixes'
import {
  ALLOWED_PHONE_NUMBER,
  type PhoneNumberSchema,
} from './phoneNumberSchema'

type Props<T extends PhoneNumberSchema = PhoneNumberSchema> = {
  isRequesting: boolean
  control: Control<T>
  error?: string
}

function PhoneNumberField<T extends PhoneNumberSchema = PhoneNumberSchema>({
  isRequesting,
  control,
  error,
}: Props<T>) {
  const { data: phonePrefixes, isLoading: isLoadingPhonePrefixes } =
    usePhonePrefixes()

  const isFormDisabled = isLoadingPhonePrefixes || isRequesting

  return (
    <>
      <FieldGroup
        className={cn(
          'flex flex flex-row gap-3 [&>*:first-child_[data-slot=select-trigger]]:rounded-r-none  [&>*:last-child_[data-slot=input]]:rounded-l-none'
        )}>
        <FormField
          control={control}
          name={'prefix' as Path<T>}
          disabled={isFormDisabled}
          render={({ field }) => (
            <FormItem className="h-[2.75rem]">
              <FieldLabel htmlFor="prefix">Predvoľba</FieldLabel>
              <Select {...field} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select prefix" />
                </SelectTrigger>
                <SelectContent className="bg-[#fff]">
                  <SelectGroup>
                    {phonePrefixes?.map((prefix) => (
                      <SelectItem
                        key={`PrefixSelectItem-${prefix}`}
                        value={`+${prefix}`}>{`+${prefix}`}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={'number' as Path<T>}
          disabled={isFormDisabled}
          render={({ field, fieldState }) => (
            <FormItem className="flex-grow">
              <FieldLabel htmlFor="number">Telefónne číslo</FieldLabel>
              <Input
                aria-invalid={!!fieldState.error}
                id="number"
                type="text"
                className="text-base! font-semibold"
                autoComplete="off"
                {...field}
                onChange={(ev) => {
                  const { value } = ev.target
                  if (value === '' || ALLOWED_PHONE_NUMBER.test(value))
                    return field.onChange(ev)
                }}
              />
            </FormItem>
          )}
        />
      </FieldGroup>
      {error && <FieldError>{error}</FieldError>}
    </>
  )
}

export { PhoneNumberField }
